import { match, P } from 'ts-pattern'

import type {
  ResolutionContext,
  SimulationExpression,
  SimulationPath
} from '../../simulation.types'
import type {
  ExpressionEvaluation,
  ExpressionEvaluator
} from '../evaluateExpression'
import { buildExpressionScope } from './buildExpressionScope'

export type BranchOutcome = 'MATCHED' | 'MISMATCHED' | 'FAILED'

/**
 * A route matches when every switch along it went the way the route needs. A
 * route with no switches on it has nothing to check, so it always matches -
 * that's a workflow that publishes or reviews whatever it's given.
 *
 * Stops at the first switch that didn't go its way, so a route ruled out early
 * doesn't pay for the conditions further along it.
 */
export const matchPath = ({
  path,
  context,
  evaluate,
  evaluations
}: {
  path: SimulationPath
  context: ResolutionContext
  evaluate: ExpressionEvaluator
  evaluations: Map<string, ExpressionEvaluation>
}): BranchOutcome =>
  path.expressions.reduce<BranchOutcome>(
    (outcome, expression) =>
      outcome === 'MATCHED'
        ? matchExpression({ expression, context, evaluate, evaluations })
        : outcome,
    'MATCHED'
  )

/**
 * Runs one switch's condition and says whether the route's branch is the one it
 * picked.
 *
 * Answers are kept in `evaluations` and reused. Two routes through the same
 * switch ask it the same question - the condition and the values it reads are
 * both fixed for a run - so the switch it belongs to is enough to key on.
 */
export const matchExpression = ({
  expression,
  context,
  evaluate,
  evaluations
}: {
  expression: SimulationExpression
  context: ResolutionContext
  evaluate: ExpressionEvaluator
  evaluations: Map<string, ExpressionEvaluation>
}): BranchOutcome => {
  const evaluation =
    evaluations.get(expression.switchTaskReferenceName) ??
    answerSwitch({ expression, context, evaluate })

  evaluations.set(expression.switchTaskReferenceName, evaluation)

  if ('failed' in evaluation) return 'FAILED'

  return isBranchTaken({ expression, groupId: evaluation.value })
    ? 'MATCHED'
    : 'MISMATCHED'
}

/**
 * Gets one switch's answer, either by running its condition or by reading the
 * value it points at.
 *
 * A `value-param` switch names one of the values it was handed and that value
 * is the answer, so there's nothing to run. Sending the name to the sandbox
 * would only get us a `ReferenceError` - it's an undeclared variable there -
 * and every route through the switch would be reported as a condition that blew
 * up.
 */
const answerSwitch = ({
  expression,
  context,
  evaluate
}: {
  expression: SimulationExpression
  context: ResolutionContext
  evaluate: ExpressionEvaluator
}): ExpressionEvaluation => {
  const scope = buildExpressionScope({ expression, context })

  return expression.isValueParam
    ? { value: valueParamAnswer(scope[expression.expression]) }
    : evaluate({ expression: expression.expression, input: scope })
}

/**
 * Conductor looks the value up among the switch's case keys, so only something
 * that could be one of those keys picks a branch. Anything else - a value that
 * never arrived, or a whole object - matches no case and takes the default,
 * which an empty answer does too.
 *
 * A quote nobody has approved is that: it has no outcome, so the branch behind
 * one is simply not taken, rather than the route failing.
 */
const valueParamAnswer = (value: unknown): string =>
  match(value)
    .with(P.string, answer => answer)
    .with(P.number, P.boolean, answer => String(answer))
    .otherwise(() => '')

/**
 * A condition group's branch is taken when the switch answers with that group.
 * The ELSE branch is taken when the answer isn't any of the groups, which is
 * what Conductor does with a value it has no case for.
 */
const isBranchTaken = ({
  expression,
  groupId
}: {
  expression: SimulationExpression
  groupId: string
}): boolean =>
  expression.result !== undefined
    ? groupId === expression.result
    : !expression.decisionCaseKeys.includes(groupId)
